"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  liveThreadId,
  liveThreadPath,
  serverLiveThreadId,
  subscribeToThreadFocus,
} from "@/lib/threadFocus";

export { enterThread } from "@/lib/threadFocus";

/**
 * Whether this card's thread is the one the reader is in — see
 * `lib/threadFocus.ts` for what that means and why it is intent rather than
 * proximity.
 *
 * The returned ref goes on the card, and up to TWO observers hang off it:
 *
 *  - The FOCUSED observer (poll gating). Only created while this thread is
 *    the live one — somebody who replies and then scrolls off to read is no
 *    longer watching for an answer — so it still costs nothing on the other
 *    nineteen cards.
 *  - The DWELL observer (OSO-622), only where a caller passes `onDwell` —
 *    the home feed's cards, which mark the post's thread as seen after the
 *    card has actually sat in the viewport for a second. Different question,
 *    different geometry: no `rootMargin`, because "has read" means the card
 *    was IN view, not one scroll-line short of it like the reading margin
 *    above.
 */
function currentPath(): string | null {
  return typeof window === "undefined" ? null : window.location.pathname;
}

/**
 * One second in view = seen. Long enough that flinging past a card on the way
 * down does not mark it, short enough that pausing on one does — and the
 * ledger behind `onDwell` makes firing again on a scroll-back free.
 */
const DWELL_MS = 1000;

export function useThreadFocus(
  postId: number,
  options: { onDwell?: () => void } = {},
): {
  live: boolean;
  cardRef: (node: HTMLElement | null) => void;
} {
  const { onDwell } = options;
  const live = useSyncExternalStore(
    subscribeToThreadFocus,
    liveThreadId,
    serverLiveThreadId,
  );
  /**
   * An intent expressed on a page the reader has left is not an intent to be
   * in this conversation now — see `livePath`.
   *
   * Read off `window` rather than `usePathname()` for two reasons: this hook
   * would otherwise pull the app router into every test that renders a card,
   * and the comparison does not need to be reactive. A route change unmounts
   * the page that made the interaction and mounts the new one, so the card
   * doing the asking is always rendering under the path it is asking about.
   */
  const focused = live === postId && liveThreadPath() === currentPath();

  // Assume visible until an observer says otherwise, so the first tick after an
  // interaction is not spent waiting for one to fire.
  const [onScreen, setOnScreen] = useState(true);
  /**
   * The node in STATE, not a ref, and the callback memoised: the observer
   * effect has to re-run when the element changes, and a ref it reads outside
   * its dependency list cannot tell it to. Today the card's root is one stable
   * `<div>` and nothing breaks — but a conditional root would leave the
   * observer bound to a detached node with `onScreen` frozen at its last
   * value, which is a bug that only appears long after the change that caused
   * it.
   */
  const [node, setNode] = useState<HTMLElement | null>(null);
  const cardRef = useCallback((next: HTMLElement | null) => setNode(next), []);

  // Each time this thread becomes the live one it starts from "visible" again
  // — otherwise a card that was scrolled away from before would come back live
  // but suppressed. Adjusted during render (React's own escape hatch for
  // deriving state from a prop), so it lands in the same commit rather than
  // one render late; `CommentItem` does the same for its deep-link expansion.
  const [wasFocused, setWasFocused] = useState(focused);
  if (focused !== wasFocused) {
    setWasFocused(focused);
    if (focused) setOnScreen(true);
  }

  useEffect(() => {
    if (!focused || !node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting ?? true),
      // Any part of the card counts, and a little beyond it: a thread whose
      // last comment is one scroll-line off the bottom is still being read.
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [focused, node]);

  /**
   * The dwell observer (OSO-622). SEPARATE from the focused one above on
   * purpose: that one exists only while the thread is live and reads a 200px
   * margin ("still reading a bit past the edge") for poll gating; this one
   * exists whenever the caller wants dwell marks and uses the bare viewport,
   * because "has seen the card" is stricter than "is still near it".
   *
   * `onDwell` may fire more than once (scroll away and back) — dedupe lives
   * in `usePostReads`' ledger, so it is passed straight through. THE CALLER
   * MUST MEMOISE `onDwell` (`useCallback`): it is in the dependency list, so
   * a new function every render tears the observer down every render, and a
   * card that re-renders faster than `DWELL_MS` (the 10 s poll makes that
   * realistic) would never complete the timer. That `useCallback` in
   * `PostCard` is load-bearing.
   */
  useEffect(() => {
    // Same jsdom guard as above: no IntersectionObserver, no dwell — tests
    // that want the timer install their own implementation.
    if (!onDwell || !node || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        // `??=`: an already-running timer keeps its start — the observer can
        // re-report "still intersecting" without restarting the clock.
        timer ??= setTimeout(onDwell, DWELL_MS);
      } else {
        clearTimeout(timer);
        timer = undefined;
      }
    });
    observer.observe(node);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [onDwell, node]);

  return { live: focused && onScreen, cardRef };
}
